import { getTenantHeaders, getCurrentTenant } from './tenant';
import { setEntraEmailDomains } from './entra-config';
import { DEFAULT_PASSWORD_POLICY, normalizePasswordPolicy, type PasswordPolicySettings } from './passwordPolicy';

export interface TenantBrandingSettings {
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  aboutImage?: string;
  fontFamily?: string;
}

// NF-001: fixed preset list — fonts are chosen from here, never a freeform string/URL,
// so the app never has to load an admin-supplied external stylesheet at runtime.
// Keep in sync with the backend validator in apps/backend/src/routes/tenants.ts and
// the <link> tags preloaded in index.html.
export const FONT_PRESETS: Record<string, { label: string; stack: string }> = {
  inter: { label: 'Inter (Modern Sans)', stack: "'Inter', ui-sans-serif, system-ui, sans-serif" },
  roboto: { label: 'Roboto (Clean Sans)', stack: "'Roboto', ui-sans-serif, system-ui, sans-serif" },
  poppins: { label: 'Poppins (Geometric Sans)', stack: "'Poppins', ui-sans-serif, system-ui, sans-serif" },
  playfair: { label: 'Playfair Display (Elegant Serif)', stack: "'Playfair Display', Georgia, serif" },
  lora: { label: 'Lora (Warm Serif)', stack: "'Lora', Georgia, serif" },
};
export const DEFAULT_FONT_PRESET = 'inter';

export interface TenantBookingSettings {
  timeSlotDuration?: number; // minutes
  advanceBookingDays?: number;
  cancellationHours?: number;
  cancellationPolicy?: string; // BO-014 review (L1): optional free-text cancellation policy for booking T&C
  autoConfirm?: boolean;
  allowCustomerCancellations?: boolean; // Allow customers to cancel their own bookings
  allowCustomerModifications?: boolean; // Allow customers to modify their booking details
  maxCartItems?: number; // Maximum items allowed in website cart
  maxGroupParticipants?: number; // BO-022 R14: max participants per group booking (min fixed at 2)
  scheduleWeekStartDay?: number; // BM-033: 0=Sun … 6=Sat
  reservationPhoneNumber?: string; // Reservations team phone for health BLOCK panels
}

export interface TenantNotificationSettings {
  emailReminders?: boolean;
  smsReminders?: boolean;
  /** BO-031 R2: reminder lead times in hours. Legacy tenants may still hold a scalar. */
  reminderHours?: number | number[];
}

/** BO-031 AC1: unknown modes stay on the R2 lead-time default. */
export function normaliseReminderTriggerMode(value: unknown): 'lead-time' | 'daily-batch' {
  return value === 'daily-batch' ? 'daily-batch' : 'lead-time'
}

/** BO-031 AC1: batch hour, tenant-local — integer 0-23, default 14 (2:00 PM). */
export function normaliseBatchHour(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return 14
  return Math.min(23, Math.max(0, Math.round(n)))
}

export const REMINDER_INTERVAL_MIN = 1;
export const REMINDER_INTERVAL_MAX = 168;
/** Gate (A-1): mirrors the backend's REMINDER_HOURS_DEFAULTS.maxIntervals — each
 *  interval is one email per booking, so the list is capped, not just bounded. */
export const REMINDER_INTERVALS_MAX_COUNT = 5;

/**
 * BO-031 R2: coerce whatever the API returned into the interval list the settings
 * form edits — a list, a pre-R2 scalar, or nothing. Mirrors the backend's
 * clampReminderHoursList (apps/backend/src/utils/tenantSettings.ts), including its
 * empties rule and interval cap, so the form shows exactly what the engine will
 * run: clamped, de-duplicated, longest lead first, at most 5 entries.
 *
 * Config-driven (Hemant): unset/empty now yields [] — no code-level 48/24
 * default. The default lives in tenant data (schema default + backfill); an
 * empty list means customers receive no reminder emails.
 */
export function normaliseReminderIntervals(value: unknown): number[] {
  const raw = Array.isArray(value) ? value : [value];
  const usable = raw
    // Number(null)/Number('') are 0 — drop empties BEFORE converting (the backend
    // does the same) so junk input never clamps up to a spurious 1h chip.
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => (typeof v === 'number' ? v : Number(v)))
    .filter((n) => Number.isFinite(n));
  if (usable.length === 0) return [];

  const clamped = usable.map((n) =>
    Math.min(REMINDER_INTERVAL_MAX, Math.max(REMINDER_INTERVAL_MIN, Math.round(n))),
  );
  return Array.from(new Set(clamped))
    .sort((a, b) => b - a)
    .slice(0, REMINDER_INTERVALS_MAX_COUNT);
}

export interface TenantPaymentSettings {
  onlinePayments?: boolean;
  partialPayments?: boolean;
  savedPaymentMethods?: boolean;
}

export interface TenantDiscountSettings {
  allowStacking?: boolean;
  maxDiscountsPerPackage?: number;
  stackWithVouchers?: boolean;
}

export interface TenantSettings {
  tenant: {
    id: string;
    businessName: string;
    description?: string;
    planType: string;
    status: string;
    currency: string;
    billingEmail?: string;
    maxStaff: number;
    maxSites: number;
  };
  branding?: TenantBrandingSettings;
  booking?: TenantBookingSettings;
  notifications?: TenantNotificationSettings;
  payment?: TenantPaymentSettings;
  discounts?: TenantDiscountSettings;
  postage?: {
    options: Array<{
      id: string;
      name: string;
      fee: number;
    }>;
  };
  products?: {
    codeFormat?: string;
    codeExample?: string;
  };
  login?: {
    passwordPolicy?: PasswordPolicySettings;
  };
  staff?: {
    entraEmailDomains?: string[];
  };
  orders?: {
    statusColors?: Record<string, string>;
  };
}

// Cache settings in memory with 5-minute TTL, keyed by tenant subdomain
const settingsCacheByTenant = new Map<string, { settings: TenantSettings; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const settingsInflightByTenant = new Map<string, Promise<TenantSettings | null>>();

/**
 * Get all tenant settings with caching
 */
export async function getTenantSettings(): Promise<TenantSettings | null> {
  const now = Date.now();
  const tenantKey = (getCurrentTenant()?.toLowerCase() || 'unknown');

  // Return cached settings if still valid for this tenant
  const cached = settingsCacheByTenant.get(tenantKey);
  if (cached && (now - cached.timestamp) < CACHE_TTL) {
    return cached.settings;
  }

  // Deduplicate concurrent callers (StrictMode remounts, RoleNav + RoleHeader)
  const inflight = settingsInflightByTenant.get(tenantKey);
  if (inflight) {
    return inflight;
  }

  const request = (async () => {
    try {
      const { API_CONFIG } = await import('./api');

      // Get current tenant from URL and normalize to lowercase
      const currentTenant = getCurrentTenant()?.toLowerCase();

      const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      const storedUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      let userRole: string | null = null;
      let isStaff = false;
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          userRole = userData.role ?? null;
          isStaff = userRole === 'STAFF' || userRole === 'SUPERVISOR' || userRole === 'MANAGER';
        } catch (e) {}
      }

      const useAuthenticatedSettings = !!token && userRole === 'TENANT_ADMIN';

      let response: Response;
      let data;

      if (useAuthenticatedSettings) {
        const headers: Record<string, string> = {
          ...getTenantHeaders(),
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };

        response = await fetch(`${API_CONFIG.BASE_URL}/tenants/settings`, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch tenant settings');
        }

        data = await response.json();
      } else {
        // Public endpoint for unauthenticated pages (registration, login branding)
        const url = `${API_CONFIG.BASE_URL}/tenants/public/settings?subdomain=${currentTenant}`;

        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          data = await response.json();
        } else {
          const headers: Record<string, string> = {
            ...getTenantHeaders(),
            'Content-Type': 'application/json',
          };

          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }

          const endpoint = isStaff ? '/tenants/settings/staff' : '/tenants/settings';

          response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
            method: 'GET',
            headers,
          });

          if (!response.ok) {
            throw new Error('Failed to fetch tenant settings');
          }

          data = await response.json();
        }
      }

      const settings = data.data.settings as TenantSettings;
      setEntraEmailDomains(settings.staff?.entraEmailDomains);
      settingsCacheByTenant.set(tenantKey, { settings, timestamp: Date.now() });

      return settings;
    } catch (error) {
      console.error('Error fetching tenant settings:', error);
      return null;
    } finally {
      settingsInflightByTenant.delete(tenantKey);
    }
  })();

  settingsInflightByTenant.set(tenantKey, request);
  return request;
}

/**
 * Get branding settings
 */
export async function getBrandingSettings(): Promise<TenantBrandingSettings> {
  const settings = await getTenantSettings();
  return settings?.branding || {
    logo: 'https://example.com/logo.png',
    primaryColor: '#6366f1',
    secondaryColor: '#8b5cf6',
    fontFamily: DEFAULT_FONT_PRESET,
  };
}

/**
 * Get booking settings
 */
export async function getBookingSettings(): Promise<TenantBookingSettings> {
  const settings = await getTenantSettings();
  return {
    timeSlotDuration: settings?.booking?.timeSlotDuration || 30,
    advanceBookingDays: settings?.booking?.advanceBookingDays || 90,
    cancellationHours: settings?.booking?.cancellationHours || 24,
    cancellationPolicy: settings?.booking?.cancellationPolicy ?? '',
    autoConfirm: settings?.booking?.autoConfirm || false,
    allowCustomerCancellations: settings?.booking?.allowCustomerCancellations ?? false,
    allowCustomerModifications: settings?.booking?.allowCustomerModifications ?? false,
    maxCartItems: settings?.booking?.maxCartItems || 6,
    maxGroupParticipants: settings?.booking?.maxGroupParticipants || 10,
    scheduleWeekStartDay: settings?.booking?.scheduleWeekStartDay ?? 1,
  };
}

/**
 * Get notification settings
 */
export async function getNotificationSettings(): Promise<TenantNotificationSettings> {
  const settings = await getTenantSettings();
  return {
    emailReminders: settings?.notifications?.emailReminders !== false,
    smsReminders: settings?.notifications?.smsReminders || false,
    reminderHours: normaliseReminderIntervals(settings?.notifications?.reminderHours),
  };
}

/**
 * Get payment settings
 * Note: Currency should be retrieved from settings.tenant.currency directly
 */
export async function getPaymentSettings(): Promise<TenantPaymentSettings> {
  const settings = await getTenantSettings();
  return {
    onlinePayments: settings?.payment?.onlinePayments !== false,
    partialPayments: settings?.payment?.partialPayments !== false,
  };
}

/**
 * Clear tenant settings cache (call after settings update)
 */
export function clearTenantSettingsCache(): void {
  settingsCacheByTenant.clear();
  settingsInflightByTenant.clear();
}

/**
 * Apply branding settings to the page
 */
// Helper function to convert hex to HSL
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  // Convert to CSS HSL format
  const hDeg = Math.round(h * 360);
  const sPercent = Math.round(s * 100);
  const lPercent = Math.round(l * 100);

  return `${hDeg} ${sPercent}% ${lPercent}%`;
}

export async function applyBrandingToPage(branding?: TenantBrandingSettings): Promise<void> {
  const brandingSettings = branding || await getBrandingSettings();

  if (brandingSettings.primaryColor) {
    const hslColor = hexToHSL(brandingSettings.primaryColor);
    document.documentElement.style.setProperty('--primary', hslColor);
  }

  if (brandingSettings.secondaryColor) {
    const hslColor = hexToHSL(brandingSettings.secondaryColor);
    document.documentElement.style.setProperty('--secondary', hslColor);
  }

  const fontStack = FONT_PRESETS[brandingSettings.fontFamily ?? DEFAULT_FONT_PRESET]?.stack
    ?? FONT_PRESETS[DEFAULT_FONT_PRESET].stack;
  document.documentElement.style.setProperty('--font-sans', fontStack);

  // Update logo if element exists
  const logoElement = document.querySelector('.tenant-logo') as HTMLImageElement;
  if (logoElement && brandingSettings.logo) {
    logoElement.src = brandingSettings.logo;
  }

  updateFavicon(brandingSettings.logo);
}

// NF-001 AC1: "logo appears in header and favicon" — reuse the tenant logo as the
// favicon rather than adding a separate asset field. Browsers cache favicons
// aggressively per-origin, so a tenant switch may need a hard refresh to show up.
const DEFAULT_FAVICON_HREF = '/favicon.ico';

function updateFavicon(logoUrl?: string): void {
  const link = document.getElementById('app-favicon') as HTMLLinkElement | null;
  if (!link) return;

  const href = logoUrl || DEFAULT_FAVICON_HREF;

  if (/\.svg(\?|$)/i.test(href)) {
    link.type = 'image/svg+xml';
  } else if (/\.png(\?|$)/i.test(href)) {
    link.type = 'image/png';
  } else if (/\.jpe?g(\?|$)/i.test(href)) {
    link.type = 'image/jpeg';
  } else if (/\.gif(\?|$)/i.test(href)) {
    link.type = 'image/gif';
  } else if (/\.webp(\?|$)/i.test(href)) {
    link.type = 'image/webp';
  } else {
    // Extensionless / unrecognized format (e.g. a CDN URL with no suffix) — let
    // the browser sniff the content type rather than asserting a wrong one.
    link.removeAttribute('type');
  }

  // A broken/unreachable/non-image tenant logo would otherwise leave the tab
  // showing a blank or broken icon; fall back to the bundled default so a bad
  // admin-supplied URL degrades gracefully instead of breaking the tab icon.
  link.onerror = () => {
    link.onerror = null;
    link.removeAttribute('type');
    link.href = DEFAULT_FAVICON_HREF;
  };

  link.href = href;
}

export async function getPasswordPolicy(): Promise<PasswordPolicySettings> {
  const settings = await getTenantSettings();
  if (settings?.login?.passwordPolicy) {
    return normalizePasswordPolicy(settings.login.passwordPolicy);
  }

  try {
    const { API_CONFIG } = await import('./api');
    const currentTenant = getCurrentTenant()?.toLowerCase();
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/auth/password-policy?tenant=${encodeURIComponent(currentTenant || '')}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getTenantHeaders(),
        },
      },
    );

    if (response.ok) {
      const data = await response.json();
      if (data?.data?.passwordPolicy) {
        return normalizePasswordPolicy(data.data.passwordPolicy);
      }
    }
  } catch {
    // Fall back to default below
  }

  return DEFAULT_PASSWORD_POLICY;
}
